import { BookOpen, ChevronRight, Lightbulb, Timer } from "lucide-react";
import type { Exercise } from "../../lib/gym/types";
import {
  getCategoryLabel,
  getDifficultyLabel,
  getTypeLabel,
} from "../../lib/gym/trainingPlan";
import {
  OPEN_STRINGS_MNEMONIC,
  OPEN_STRINGS_ORDER_SHORT,
  OPEN_STRINGS_REFERENCE,
} from "../../lib/gym/guitarBasicsReference";
import { GYM_STUDIO as C } from "../../lib/gym/gymStudioTheme";
import { BEGINNER_PATH_THEME as PATH } from "../../lib/beginnerPath/beginnerPathTheme";
import { Button } from "../ui/button";

export interface GymIntroModalProps {
  exercise: Exercise;
  usesAudioInput: boolean;
  wasCompletedBefore: boolean;
  onBegin: () => void;
  /** Camino: cheatsheet de cuerdas + tip del profe. */
  pathMode?: boolean;
  onOpenReference?: () => void;
}

export function GymIntroModal({
  exercise,
  usesAudioInput,
  wasCompletedBefore,
  onBegin,
  pathMode = false,
  onOpenReference,
}: GymIntroModalProps) {
  const accent = pathMode ? "#99f6e4" : C.accent;
  const accentSoft = pathMode ? "rgba(153, 246, 228, 0.12)" : C.accentSoft;
  const accentGlow = pathMode ? "rgba(153, 246, 228, 0.28)" : C.accentGlow;
  const borderHover = pathMode ? PATH.cardBorderMint : C.borderHover;
  const textMain = pathMode ? PATH.text : C.textMain;
  const textMuted = pathMode ? PATH.textMuted : C.textMuted;
  const textDim = pathMode ? PATH.textDim : C.textDim;

  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center p-4 sm:p-8 gym-intro-enter">
      <div className="absolute inset-0 bg-black/62" aria-hidden />

      <article
        role="dialog"
        aria-labelledby="gym-intro-title"
        className="relative z-10 w-full max-w-2xl max-h-[min(92vh,720px)] overflow-y-auto rounded-2xl p-5 sm:p-7 shadow-2xl gym-intro-card"
        style={{
          background: "linear-gradient(165deg, rgba(42,38,36,0.97) 0%, rgba(28,26,25,0.98) 100%)",
          border: `1px solid ${borderHover}`,
          boxShadow: `0 24px 80px rgba(0,0,0,0.55), 0 0 0 1px ${accentSoft}, inset 0 1px 0 rgba(255,255,255,0.06)`,
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="flex flex-wrap gap-1.5">
            <Tag accent={accent} accentSoft={accentSoft} textDim={textDim}>
              {getDifficultyLabel(exercise.difficulty)}
            </Tag>
            <Tag muted accent={accent} accentSoft={accentSoft} textDim={textDim}>
              {getCategoryLabel(exercise.category)}
            </Tag>
            <Tag muted accent={accent} accentSoft={accentSoft} textDim={textDim}>
              {getTypeLabel(exercise.type)}
            </Tag>
          </div>
          {onOpenReference && (
            <button
              type="button"
              onClick={onOpenReference}
              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-lg hover:bg-white/5"
              style={{ color: accent, border: `1px solid ${accent}44` }}
            >
              <BookOpen className="w-3 h-3" />
              Cuerdas
            </button>
          )}
        </div>

        <h2 id="gym-intro-title" className="text-lg font-bold mb-1" style={{ color: textMain }}>
          {exercise.title}
        </h2>
        <p className="text-xs mb-3" style={{ color: textDim }}>
          {exercise.subtitle}
        </p>

        <p className="text-sm leading-relaxed mb-4" style={{ color: textMuted }}>
          {exercise.description}
        </p>

        {(pathMode || usesAudioInput) && (
          <div
            className="mb-4 rounded-xl px-3 py-3"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: `1px solid ${pathMode ? PATH.cardBorder : C.border}`,
            }}
          >
            <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: textDim }}>
              Orden de cuerdas (al aire)
            </p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {OPEN_STRINGS_REFERENCE.map((s) => (
                <span
                  key={s.string}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium tabular-nums"
                  style={{ background: accentSoft, color: accent }}
                >
                  <span className="opacity-70">{s.string}</span>
                  {s.note}
                </span>
              ))}
            </div>
            <p className="text-[11px] leading-snug" style={{ color: textDim }}>
              {OPEN_STRINGS_ORDER_SHORT}
            </p>
            <p className="text-[10px] mt-1.5 leading-snug" style={{ color: textMuted }}>
              {OPEN_STRINGS_MNEMONIC}
            </p>
          </div>
        )}

        <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: textDim }}>
          Instrucciones
        </p>
        <ul className="space-y-2 mb-4">
          {exercise.instructions.map((inst, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm" style={{ color: textMuted }}>
              <span
                className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5"
                style={{ backgroundColor: accentSoft, color: accent }}
              >
                {i + 1}
              </span>
              {inst}
            </li>
          ))}
        </ul>

        {exercise.professorHint && (
          <div
            className="mb-4 flex gap-2 rounded-xl px-3 py-2.5 text-xs leading-relaxed"
            style={{
              background: "rgba(251, 191, 36, 0.08)",
              border: "1px solid rgba(251, 191, 36, 0.22)",
              color: "rgba(253, 230, 138, 0.95)",
            }}
          >
            <Lightbulb className="w-4 h-4 shrink-0 mt-0.5 text-amber-300/90" />
            <p>{exercise.professorHint}</p>
          </div>
        )}

        {usesAudioInput && (
          <p
            className="text-xs mb-4 px-3 py-2.5 rounded-xl leading-relaxed"
            style={{ backgroundColor: accentSoft, color: accent, border: `1px solid ${accent}33` }}
          >
            Toca tu guitarra: el micrófono reconoce la nota. La cuerda resaltada en el mástil es tu
            objetivo.
          </p>
        )}

        <p className="text-[10px] mb-5 flex items-center gap-2" style={{ color: textDim }}>
          <Timer className="w-3.5 h-3.5 shrink-0" />
          ~{exercise.timeEstimate} min · {exercise.methodRef}
        </p>

        <Button
          onClick={onBegin}
          className="w-full font-semibold border-0 h-11"
          style={{
            background: pathMode ? PATH.btnMint : accent,
            color: pathMode ? "#100f0e" : "#fff",
            boxShadow: `0 8px 28px ${accentGlow}`,
          }}
          size="lg"
        >
          {wasCompletedBefore ? "Repetir ejercicio" : "Comenzar ejercicio"}
          <ChevronRight className="w-4 h-4" />
        </Button>
      </article>
      <style>{`
        .gym-intro-enter { animation: gymIntroFade 0.28s ease-out both; }
        .gym-intro-card { animation: gymIntroCard 0.34s cubic-bezier(0.22, 1, 0.36, 1) both; }
        @keyframes gymIntroFade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes gymIntroCard {
          from { opacity: 0; transform: translateY(18px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

function Tag({
  children,
  muted,
  accent,
  accentSoft,
  textDim,
}: {
  children: React.ReactNode;
  muted?: boolean;
  accent: string;
  accentSoft: string;
  textDim: string;
}) {
  return (
    <span
      className="text-[10px] px-1.5 py-0.5 rounded font-medium"
      style={{
        backgroundColor: muted ? "rgba(255,255,255,0.06)" : accentSoft,
        color: muted ? textDim : accent,
      }}
    >
      {children}
    </span>
  );
}
